/**
 * Confidence → badge tone, kept out of the component so it is unit-testable
 * (and so MatchBadge.tsx exports only a component, per the repo's
 * catalogRowOrder.ts / saveEligibility.ts pattern).
 */

/** At or above this a match is confident — green. */
export const MATCH_CONFIDENT_THRESHOLD = 0.9
/**
 * Below this a match is weak — red. This is also the matcher's own default
 * save threshold (`entity_matching/schemas.py`, confidence_threshold=0.5), so
 * anything under it is by definition something the matcher declined to
 * persist, which is exactly what the Pending tab lists.
 *
 * PendingTable leans on that equality to tell the user a suggestion is too
 * weak for "Run matching" to ever act on — measured at 82% of the suggestions
 * shown, so it is the common case, not an edge one.
 *
 * Scope that claim precisely: the threshold is a per-request PARAMETER, not a
 * global invariant, so a caller passing a lower one would match things this
 * calls weak. It holds for the Run action on this page because that action
 * deliberately sends no threshold and therefore gets the default. If either
 * the backend default or that call changes, move this with it or the hint
 * starts lying.
 */
export const MATCH_WEAK_THRESHOLD = 0.5

export type MatchTone = 'strong' | 'medium' | 'weak'

export const toneFor = (confidence: number): MatchTone => {
  if (confidence >= MATCH_CONFIDENT_THRESHOLD) return 'strong'
  if (confidence >= MATCH_WEAK_THRESHOLD) return 'medium'
  return 'weak'
}

/**
 * 0.98 -> 98. Floors rather than rounds so a 0.999 score never reads as a flat
 * 100% match, and clamps so a stray out-of-range score can't render "-40%".
 */
export const toPercent = (confidence: number): number => {
  if (!Number.isFinite(confidence)) return 0
  return Math.max(0, Math.min(100, Math.floor(confidence * 100)))
}

/** `'manual'` means a person made the connection — no percentage applies. */
export const isManualMatch = (matchMethod?: string | null): boolean =>
  (matchMethod || '').toLowerCase() === 'manual'
